<template>
    <span>
        <v-btn outlined :disabled="!competence" @click="open">
            <v-icon left small>mdi-link-variant</v-icon>Link de envio
        </v-btn>

        <v-dialog v-model="dialog" max-width="1040" scrollable>
            <v-card class="rma-link">
                <!-- Cabecalho -->
                <header class="rma-link__header">
                    <div>
                        <div class="rma-link__title">
                            Link de envio
                            <span v-if="competence" class="rma-link__competence">{{ competence.label }}</span>
                        </div>
                        <p class="rma-link__intro">
                            Para quem não tem acesso ao Painel da Devedora enviar os documentos desta competência, sem login.
                        </p>
                    </div>
                    <v-btn icon small @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </header>

                <v-card-text class="rma-link__body">
                    <!-- Novo link -->
                    <section v-if="$allowed('rma.request')" class="rma-link__panel">
                        <template v-if="!created">
                            <div class="rma-link__section-title">Novo link</div>
                            <v-form ref="form" class="rma-link__form" @submit.prevent="create">
                                <div class="rma-link__field">
                                    <label class="rma-link__label">Para quem <span>(opcional)</span></label>
                                    <v-text-field v-model="form.label" placeholder="Ex.: Contador João" outlined dense hide-details="auto" maxlength="150" />
                                </div>
                                <div class="rma-link__field rma-link__field--email">
                                    <label class="rma-link__label">E-mail <span>(opcional)</span></label>
                                    <v-text-field v-model="form.email" placeholder="nome@empresa.com.br" type="email" outlined dense hide-details="auto" maxlength="255" :rules="[validEmail]" />
                                </div>
                                <div class="rma-link__field rma-link__field--days">
                                    <label class="rma-link__label">Validade</label>
                                    <v-select v-model="form.days" :items="dayOptions" item-value="value" item-text="text" outlined dense hide-details="auto" />
                                </div>
                                <div v-if="companies.length > 1" class="rma-link__field rma-link__field--company">
                                    <label class="rma-link__label">Empresa</label>
                                    <v-select v-model="form.company_id" :items="companyOptions" item-value="id" item-text="name" outlined dense hide-details="auto" />
                                </div>
                                <div class="rma-link__submit">
                                    <v-btn color="primary" depressed type="submit" :loading="creating"><v-icon left small>mdi-link-plus</v-icon>Gerar link</v-btn>
                                </div>
                            </v-form>
                        </template>

                        <!-- Link recem-gerado: so aparece agora (o sistema guarda apenas o hash) -->
                        <template v-else>
                            <div class="rma-link__created-head">
                                <v-icon color="#16a34a">mdi-check-circle</v-icon>
                                <div>
                                    <div class="rma-link__section-title mb-0">Link gerado{{ created.label ? ` para ${created.label}` : "" }}</div>
                                    <div class="rma-link__muted">Vale até {{ shortDateTime(created.expires_at) }}. Copie agora: ele não aparece de novo.</div>
                                </div>
                            </div>
                            <div class="rma-link__url-row">
                                <v-text-field :value="created.url" readonly outlined dense hide-details class="rma-link__url" @focus="$event.target.select()" />
                                <v-btn color="primary" depressed @click="copy(created.url, 'Link copiado.')"><v-icon left small>mdi-content-copy</v-icon>Copiar link</v-btn>
                            </div>
                            <div class="rma-link__created-actions">
                                <v-btn outlined small @click="copy(message, 'Mensagem copiada. Cole no WhatsApp.')"><v-icon left small>mdi-message-text-outline</v-icon>Copiar mensagem</v-btn>
                                <v-btn outlined small color="success" :href="whatsappUrl" target="_blank" rel="noopener"><v-icon left small>mdi-whatsapp</v-icon>Abrir no WhatsApp</v-btn>
                                <v-spacer></v-spacer>
                                <v-btn text small color="primary" @click="created = null"><v-icon left small>mdi-plus</v-icon>Gerar outro link</v-btn>
                            </div>
                        </template>
                    </section>

                    <!-- Links desta competencia -->
                    <section class="rma-link__list">
                        <div class="rma-link__list-head">
                            <div class="rma-link__section-title mb-0">
                                Links gerados <span class="rma-link__count">{{ filteredLinks.length }}</span>
                            </div>
                            <div class="rma-link__filters">
                                <v-text-field v-model="filters.name" placeholder="Filtrar por nome" prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable class="rma-link__filter" />
                                <v-text-field v-model="filters.email" placeholder="Filtrar por e-mail" prepend-inner-icon="mdi-email-outline" outlined dense hide-details clearable class="rma-link__filter" />
                            </div>
                        </div>

                        <v-data-table :headers="headers" :items="filteredLinks" :loading="loading" :options.sync="tableOptions"
                            :footer-props="{ itemsPerPageOptions: [5, 10, 25], itemsPerPageText: 'Por página', pageText: '{0}-{1} de {2}' }"
                            no-data-text="Nenhum link gerado nesta competência." no-results-text="Nenhum link encontrado para o filtro." loading-text="Carregando..."
                            class="rma-link__table">
                            <template v-slot:[`item.label`]="{ item }">
                                <span class="rma-link__name">{{ item.label || "Sem nome" }}</span>
                            </template>
                            <template v-slot:[`item.email`]="{ item }">
                                <span v-if="item.email" class="rma-link__nowrap">{{ item.email }}</span>
                                <span v-else class="rma-link__muted">—</span>
                            </template>
                            <template v-slot:[`item.company_name`]="{ item }">{{ item.company_name || "Todas" }}</template>
                            <template v-slot:[`item.expires_at`]="{ item }">
                                <div class="rma-link__nowrap">até {{ shortDateTime(item.expires_at) }}</div>
                                <div class="rma-link__sub" :title="item.created_by_name ? `Gerado por ${item.created_by_name} em ${shortDateTime(item.created_at)}` : ''">gerado em {{ shortDate(item.created_at) }}</div>
                            </template>
                            <template v-slot:[`item.state`]="{ item }">
                                <ui-badge :text="STATES[item.state].text" :tone="STATES[item.state].tone" />
                                <div v-if="item.last_used_at" class="rma-link__sub">aberto em {{ shortDateTime(item.last_used_at) }}</div>
                            </template>
                            <template v-slot:[`item.uploads`]="{ item }">
                                <span :class="{ 'rma-link__muted': !item.uploads }">{{ item.uploads }}</span>
                            </template>
                            <template v-slot:[`item.actions`]="{ item }">
                                <v-btn v-if="item.state === 'active' && $allowed('rma.request')" icon small color="error" title="Cancelar link" aria-label="Cancelar link"
                                    :loading="revoking === item.id" @click="revoke(item)"><v-icon small>mdi-link-off</v-icon></v-btn>
                            </template>
                        </v-data-table>
                    </section>
                </v-card-text>
            </v-card>
        </v-dialog>
    </span>
</template>

<script>
import axios from "axios";
import { formatDateTime, notifyError, notifySuccess } from "@/components/rma/rma.util";

const STATES = {
    active: { text: "Ativo", tone: "success" },
    expired: { text: "Vencido", tone: "neutral" },
    revoked: { text: "Cancelado", tone: "danger" },
    closed: { text: "Envio concluído", tone: "info" },
};

// RMA: link de envio sem login (para mandar por WhatsApp).
export default {
    name: "RmaShareLinkButton",

    props: {
        competence: { type: Object, default: null },
        companies: { type: Array, default: () => [] },
    },

    data: () => ({
        STATES,
        dialog: false,
        loading: false,
        creating: false,
        revoking: null,
        links: [],
        created: null,
        form: { label: "", email: "", company_id: null, days: 2 },
        filters: { name: "", email: "" },
        tableOptions: { page: 1, itemsPerPage: 5 },
        dayOptions: [1, 2, 3, 5, 7, 15, 30].map((days) => ({ value: days, text: days === 1 ? "1 dia" : `${days} dias` })),
    }),

    computed: {
        headers() {
            const headers = [
                { text: "Para quem", value: "label", sortable: false },
                { text: "E-mail", value: "email", sortable: false },
                { text: "Empresa", value: "company_name", sortable: false },
                { text: "Validade", value: "expires_at", sortable: false },
                { text: "Situação", value: "state", sortable: false },
                { text: "Arquivos", value: "uploads", sortable: false, align: "center", width: 90 },
                { text: "", value: "actions", sortable: false, align: "end", width: 48 },
            ];
            return this.companies.length > 1 ? headers : headers.filter((header) => header.value !== "company_name");
        },
        // Filtros locais (a lista e so desta competencia).
        filteredLinks() {
            const plain = (value) => String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
            const name = plain(this.filters.name);
            const email = plain(this.filters.email);
            return this.links.filter((link) => (!name || plain(link.label).includes(name)) && (!email || plain(link.email).includes(email)));
        },
        companyOptions() {
            return [{ id: null, name: "Todas as empresas" }].concat(this.companies.map((company) => ({ id: company.id, name: company.name })));
        },
        message() {
            if (!this.created) return "";
            return `Olá! Segue o link para enviar os documentos da fiscalização mensal (RMA) de ${this.competence.label}: ${this.created.url}\n`
                + `O link vale até ${formatDateTime(this.created.expires_at)}. Basta abrir e anexar cada documento. — Brizola e Japur Administração Judicial`;
        },
        whatsappUrl() {
            return `https://wa.me/?text=${encodeURIComponent(this.message)}`;
        },
    },

    methods: {
        formatDateTime,

        // "26/10/2026 15:44" (sem a virgula do toLocaleString).
        shortDateTime(value) {
            return formatDateTime(value).replace(",", "");
        },

        shortDate(value) {
            return formatDateTime(value).split(",")[0];
        },

        open() {
            this.dialog = true;
            this.created = null;
            this.form = { label: "", email: "", company_id: null, days: 2 };
            this.filters = { name: "", email: "" };
            this.tableOptions = { ...this.tableOptions, page: 1 };
            this.load();
        },

        async load() {
            this.loading = true;
            try {
                const result = await axios.get(`/api/admin/rma/competences/${this.competence.id}/share-links`);
                this.links = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        validEmail(value) {
            return !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim()) || "E-mail inválido";
        },

        async create() {
            if (this.$refs.form && !this.$refs.form.validate()) return;
            this.creating = true;
            try {
                const result = await axios.post(`/api/admin/rma/competences/${this.competence.id}/share-links`, this.form);
                this.created = result.data.content;
                this.form.label = "";
                this.form.email = "";
                this.tableOptions = { ...this.tableOptions, page: 1 };
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.creating = false;
            }
        },

        async revoke(link) {
            const proceed = await this.$dialog.confirm({
                title: "Cancelar link",
                text: `O link${link.label ? ` de ${link.label}` : ""} deixa de abrir na hora. Os arquivos já enviados continuam no checklist.`,
                actions: { false: "Voltar", true: "Cancelar link" },
            });
            if (!proceed) return;
            this.revoking = link.id;
            try {
                await axios.post(`/api/admin/rma/share-links/${link.id}/revoke`);
                notifySuccess(this, "Link cancelado.");
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.revoking = null;
            }
        },

        async copy(text, message) {
            try {
                await navigator.clipboard.writeText(text);
                notifySuccess(this, message);
            } catch (error) {
                notifyError(this, new Error("Não foi possível copiar. Selecione o texto e copie manualmente."));
            }
        },
    },
};
</script>

<style scoped>
.rma-link { border-radius: 14px !important; }
.rma-link__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 20px 24px 14px; border-bottom: 1px solid #eaecf0; }
.rma-link__title { display: flex; align-items: center; gap: 10px; font-size: 17px; font-weight: 600; color: #101828; }
.rma-link__competence { padding: 2px 10px; border-radius: 999px; background: #eef2ff; color: #27306b; font-size: 12.5px; font-weight: 600; }
.rma-link__intro { margin: 4px 0 0; font-size: 13px; line-height: 1.5; color: #667085; }
.rma-link__body { padding: 20px 24px 12px !important; }

.rma-link__section-title { font-size: 13px; font-weight: 600; color: #344054; margin-bottom: 12px; }
.rma-link__muted { font-size: 12.5px; color: #98a2b3; }
.rma-link__sub { font-size: 12px; color: #98a2b3; line-height: 1.4; margin-top: 2px; white-space: nowrap; }
.rma-link__nowrap { white-space: nowrap; }

/* Novo link */
.rma-link__panel { padding: 16px; border: 1px solid #eaecf0; border-radius: 12px; background: #f9fafb; }
.rma-link__form { display: grid; grid-template-columns: 1fr 1.3fr 150px auto; gap: 12px; align-items: end; }
.rma-link__field--company { grid-column: 1 / span 3; }
.rma-link__label { display: block; font-size: 12.5px; font-weight: 500; color: #344054; margin-bottom: 4px; }
.rma-link__label span { font-weight: 400; color: #98a2b3; }
.rma-link__field ::v-deep .v-input__slot { background: #fff !important; }
.rma-link__submit .v-btn { height: 40px !important; }

.rma-link__created-head { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; }
.rma-link__url-row { display: flex; gap: 8px; align-items: center; }
.rma-link__url { font-size: 13px; }
.rma-link__url ::v-deep .v-input__slot { background: #fff !important; }
.rma-link__url-row .v-btn { height: 40px !important; }
.rma-link__created-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; align-items: center; }

/* Lista */
.rma-link__list { margin-top: 24px; }
.rma-link__list-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 10px; }
.rma-link__count { margin-left: 4px; padding: 0 8px; border-radius: 999px; background: #f2f4f7; color: #475467; font-size: 12px; font-weight: 600; }
.rma-link__filters { display: flex; gap: 8px; }
.rma-link__filter { width: 210px; flex: 0 0 auto; font-size: 13px; }
.rma-link__name { font-weight: 500; color: #101828; white-space: nowrap; }
.rma-link__table { border: 1px solid #eaecf0; border-radius: 10px; overflow: hidden; }
.rma-link__table ::v-deep .v-data-table__wrapper { overflow-x: auto; }
.rma-link__table ::v-deep th { font-size: 12px !important; font-weight: 600 !important; color: #667085 !important; background: #f9fafb; white-space: nowrap; }
.rma-link__table ::v-deep td { font-size: 13px !important; color: #344054; vertical-align: middle; padding-top: 10px !important; padding-bottom: 10px !important; height: auto !important; }

@media (max-width: 760px) {
    .rma-link__form { grid-template-columns: 1fr; }
    .rma-link__field--company { grid-column: auto; }
    .rma-link__filters { width: 100%; }
    .rma-link__filter { flex: 1 1 0; width: auto; }
}
</style>
